// translations/pages/kyveil/fr.js

export const kyveilPageFR = {
  "seo": {
    "title": "Kyveil : anonymisation des données personnelles | pAIoneer Labs GmbH",
    "description": "Kyveil détecte et anonymise les données personnelles (PII) localement sur vos systèmes. Application de bureau ou API REST pour une anonymisation conforme au RGPD.",
    "keywords": "Kyveil, anonymisation PII, anonymiser les données personnelles, protection des données, RGPD, GDPR, Microsoft Presidio, spaCy, flair, traitement local des données",
    "ogTitle": "Kyveil : anonymisation professionnelle des données personnelles avec IA et NLP",
    "ogDescription": "Détection et anonymisation automatiques des données personnelles, sous forme d’application de bureau ou d’API REST sur votre propre infrastructure.",
    "twitterTitle": "Kyveil : anonymisation des données personnelles",
    "twitterDescription": "Détection et anonymisation locales des données personnelles dans les documents sensibles.",
    "url": "https://paioneer-labs.eu/kyveil",
    "structuredData": {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Kyveil",
      "applicationCategory": "SecurityApplication",
      "operatingSystem": "Windows, macOS, Linux, Server",
      "description": "Logiciel de détection et d’anonymisation locales des données personnelles avec IA et NLP.",
      "provider": {
        "@type": "Organization",
        "name": "pAIoneer Labs GmbH",
        "url": "https://paioneer-labs.eu"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "url": "https://paioneer-labs.eu/kyveil"
      }
    }
  },
  "hero": {
    "eyebrow": "Protection des données et anonymisation",
    "title": "Kyveil",
    "subtitle": "Anonymisation locale avant l’IA, les tests et le partage.",
    "description": "Kyveil est une couche d’anonymisation locale pour les documents et les flux de travail d’IA. Le logiciel détecte les données personnelles dans les fichiers Word, Excel, PDF et texte, les remplace ou les masque et affiche un aperçu avant que le contenu n’entre dans des chats d’IA, des tests, des analyses ou un partage externe. Kyveil est développé et continuellement amélioré de manière [pilotée par l’IDD](idd) (Intent-Driven Development) et automatisée avec [Veridion](veridion) par nos collaborateurs IA avec l’humain dans la boucle.",
    "platforms": "Application de bureau, API REST et AnonChat",
    "proofPoints": [
      "Les documents restent en local sur vos systèmes.",
      "Les données personnelles sont remplacées ou masquées.",
      "Un aperçu montre le contenu anonymisé avant le partage ou le chat IA.",
      "Word, Excel, PDF et texte sont les flux documentaires centraux."
    ]
  },
  "dataSovereignty": {
    "title": "Vos données. Vos systèmes. Votre contrôle.",
    "subtitle": "Une souveraineté totale sur vos données grâce au traitement local.",
    "items": [
      {
        "title": "Aucun cloud requis",
        "description": "L’ensemble du traitement s’effectue localement sur votre infrastructure. Vos fichiers ne quittent jamais vos systèmes. Aucune dépendance au cloud, aucune transmission de données vers l’extérieur."
      },
      {
        "title": "Edge computing",
        "description": "L’application de bureau fonctionne de manière entièrement autonome sur les postes de travail. L’API est exploitée sur vos propres serveurs. Les deux variantes traitent toutes les données localement, à l’intérieur de votre périmètre de sécurité."
      },
      {
        "title": "Autonomie des données",
        "description": "Un contrôle total de vos flux de données. Aucune dépendance à un fournisseur. Aucun coût de cloud récurrent. Vos informations sensibles restent exclusivement sous votre contrôle."
      },
      {
        "title": "Matériel standard",
        "description": "Aucun GPU requis. Fonctionne sur des ordinateurs de bureau courants et des serveurs standard. Aucun investissement dans du matériel spécialisé n’est nécessaire."
      }
    ]
  },
  "features": {
    "title": "Fonctionnalités clés",
    "subtitle": "Tout ce qu’il faut pour une détection et une anonymisation maîtrisées des données personnelles dans les processus d’entreprise.",
    "items": [
      {
        "title": "Détection des PII par l’IA",
        "description": "Reconnaissance automatique avec Microsoft Presidio SDK, spaCy d’Explosion AI et flair de la Humboldt-Universität zu Berlin.",
        "tone": "primary"
      },
      {
        "title": "Mode Replace",
        "description": "Remplace les PII détectées par des marqueurs génériques tels que [NAME], [EMAIL] et [ADDRESS], pour les scénarios de conformité.",
        "tone": "info"
      },
      {
        "title": "Mode Fake",
        "description": "Remplace les PII détectées par des valeurs de substitution plausibles, pour les environnements de test et le développement.",
        "tone": "warning"
      },
      {
        "title": "Plusieurs formats de documents",
        "description": "Traite les fichiers Microsoft Word, Excel, OpenDocument et PDF ainsi que le texte brut.",
        "tone": "success"
      },
      {
        "title": "Prise en charge multilingue",
        "description": "Prise en charge intégrée de l’allemand, de l’anglais, du français, de l’italien et de l’espagnol, avec des modèles NLP propres à chaque langue.",
        "tone": "secondary"
      },
      {
        "title": "Détection configurable",
        "description": "Définissez les entités PII à détecter : noms, adresses, e-mails, numéros de téléphone, comptes bancaires, numéros fiscaux, etc.",
        "tone": "primary"
      },
      {
        "title": "Extensibilité",
        "description": "Des interfaces permettent d’ajouter votre propre logique de prétraitement et de post-traitement pour l’intégration dans vos flux de travail spécifiques.",
        "tone": "info"
      },
      {
        "title": "Optimisé pour le CPU",
        "description": "Une architecture de modèles efficace, qui fonctionne sur du matériel de bureau standard sans GPU.",
        "tone": "warning"
      }
    ]
  },
  "technology": {
    "title": "Socle technologique",
    "subtitle": "Des frameworks de niveau entreprise et une recherche NLP éprouvée.",
    "items": [
      {
        "title": "Microsoft Presidio SDK",
        "description": "Microsoft Data Protection and De-identification SDK. Framework de référence du secteur pour la détection et l’anonymisation des PII, avec des reconnaisseurs d’entités intégrés.",
        "tone": "primary"
      },
      {
        "title": "spaCy",
        "subtitle": "Développé par Explosion AI",
        "description": "Industrial-Strength Natural Language Processing. Bibliothèque NLP prête pour la production, avec des modèles statistiques pré-entraînés pour la reconnaissance d’entités nommées.",
        "tone": "info"
      },
      {
        "title": "flair",
        "subtitle": "Développé par la Humboldt-Universität zu Berlin",
        "description": "Framework NLP à l’état de l’art, avec des plongements contextuels de chaînes de caractères. Des modèles de réseaux de neurones avancés pour une détection d’entités plus précise.",
        "tone": "secondary"
      }
    ],
    "integration": "Pipeline IA intégré",
    "integrationDesc": "Détection combinée, avec Presidio comme couche d’orchestration et les modèles spaCy et flair, pour une détection optimale dans plusieurs langues et pour plusieurs types d’entités."
  },
  "technicalDetails": {
    "title": "Capacités techniques",
    "subtitle": "Détection configurable pour les documents d’entreprise européens.",
    "items": [
      {
        "title": "Prise en charge des langues",
        "description": "Modèles NLP pré-entraînés pour l’allemand, l’anglais, le français, l’italien et l’espagnol. Reconnaissance d’entités propre à chaque langue pour une détection précise des PII dans les contextes professionnels européens.",
        "tone": "primary"
      },
      {
        "title": "Entités PII configurables",
        "description": "Choisissez les types de données personnelles à détecter et à anonymiser : noms de personnes, adresses e-mail, numéros de téléphone, adresses postales, comptes bancaires IBAN, numéros de carte de crédit, numéros de sécurité sociale, numéros fiscaux, numéros de passeport, etc.",
        "tone": "info"
      },
      {
        "title": "Modes d’anonymisation",
        "description": "Le mode Replace remplace les PII par des marqueurs génériques pour la documentation de conformité. Le mode Fake utilise des valeurs de substitution plausibles pour les environnements de test, tout en conservant la structure du document.",
        "tone": "warning"
      },
      {
        "title": "Prétraitement et post-traitement",
        "description": "Implémentez votre propre logique avant et après l’anonymisation. Ajoutez la validation de documents, la conversion de formats, des contrôles de qualité, la journalisation d’audit ou l’intégration avec des systèmes en aval grâce aux interfaces fournies.",
        "tone": "secondary"
      }
    ]
  },
  "anonChat": {
    "title": "AnonChat complète Kyveil par un chat IA web",
    "subtitle": "La nouvelle application utilise la même approche d’anonymisation, en amont du chat proprement dit avec des systèmes d’IA externes ou internes.",
    "eyebrow": "Nouvelle application",
    "name": "AnonChat",
    "description": "AnonChat est un chat IA web dans lequel les documents Word et Excel peuvent être anonymisés avant d’être utilisés avec ChatGPT, Claude, Gemini ou des LLM internes. Un aperçu montre le texte anonymisé avant que les contenus ne soient repris dans le chat.",
    "statement": "La décision reste ainsi entre les mains de l’utilisateur : vérifier, valider, et seulement ensuite converser.",
    "steps": [
      {
        "title": "Charger le document",
        "description": "Les fichiers Word ou Excel sont préparés pour l’anonymisation avant d’entrer dans le flux de travail IA.",
        "tone": "primary"
      },
      {
        "title": "Anonymiser les PII",
        "description": "Kyveil détecte les données personnelles et les remplace ou les masque selon les règles choisies.",
        "tone": "info"
      },
      {
        "title": "Vérifier l’aperçu",
        "description": "Le texte anonymisé est visible avant que les contenus ne soient transmis au chat.",
        "tone": "warning"
      },
      {
        "title": "Utiliser le chat IA",
        "description": "Ce n’est qu’après vérification que les contenus anonymisés sont utilisés avec des LLM externes ou internes.",
        "tone": "success"
      }
    ]
  },
  "deployment": {
    "title": "Options de déploiement",
    "subtitle": "Choisissez la solution adaptée à votre cas d’usage.",
    "options": [
      {
        "title": "Application de bureau",
        "subtitle": "Application autonome pour postes de travail individuels",
        "description": "La version de bureau fonctionne de manière entièrement autonome sous Windows, macOS ou Linux. Idéale pour les utilisateurs qui doivent anonymiser des documents directement sur leur poste de travail, sans dépendance au réseau.",
        "features": [
          "Fonctionnement 100 % hors ligne - aucune connexion Internet requise",
          "Les fichiers ne quittent jamais le poste de travail local",
          "Traitement par lots de plusieurs fichiers",
          "Interface graphique pour une utilisation simple",
          "Fonctionne sur des ordinateurs de bureau standard sans GPU",
          "Licence unique - pas d’abonnement"
        ]
      },
      {
        "title": "API REST",
        "subtitle": "Déploiement sur serveur pour l’intégration et la montée en charge",
        "description": "La version API est exploitée sur vos propres serveurs ou sur une infrastructure de cloud privé. Idéale pour intégrer l’anonymisation dans des systèmes et des flux de travail existants à fort volume de traitement.",
        "features": [
          "Déploiement sur votre propre infrastructure - sur site ou dans un cloud privé",
          "Mise à l’échelle horizontale pour les exigences de débit élevé",
          "API RESTful avec une documentation complète",
          "Même moteur d’anonymisation que la version de bureau",
          "Exemples d’intégration pour les plateformes courantes",
          "Prise en charge des adaptations de prétraitement et de post-traitement"
        ]
      },
      {
        "title": "AnonChat",
        "subtitle": "Chat IA web avec anonymisation préalable",
        "description": "AnonChat place Kyveil en amont du chat IA proprement dit. Les documents Word et Excel sont anonymisés, un aperçu est vérifié, et ce n’est qu’ensuite que les contenus sont utilisés avec ChatGPT, Claude, Gemini ou des LLM internes.",
        "features": [
          "Anonymisation avant le chat plutôt que contrôle a posteriori",
          "Aperçu du texte anonymisé avant sa transmission",
          "Conçu pour les questions posées aux systèmes d’IA à partir de documents",
          "Réduit le risque d’envoyer des données personnelles à des modèles externes",
          "Peut être raccordé à des LLM internes ou à des environnements de chat d’entreprise",
          "Repose sur la même approche d’anonymisation que l’application de bureau et l’API REST"
        ]
      }
    ],
    "bothNote": "L’application de bureau, l’API REST et AnonChat sont conçus pour anonymiser les contenus sensibles de manière contrôlée avant tout traitement ultérieur."
  },
  "useCases": {
    "title": "Cas d’usage",
    "subtitle": "De la conformité au RGPD à des données de développement sûres.",
    "items": [
      {
        "title": "Conformité au RGPD",
        "description": "Anonymisez les données personnelles avant de transmettre des documents à l’extérieur ou à des services internes. Respectez les exigences de protection des données pour les données de test, les jeux de données d’analyse et la documentation.",
        "tone": "primary"
      },
      {
        "title": "Préparation de données de test",
        "description": "Préparez des jeux de données de test réalistes afin que les développeurs puissent travailler avec des données proches de la production sans exposer de véritables informations personnelles.",
        "tone": "info"
      },
      {
        "title": "Traitement des documents RH",
        "description": "Anonymisez les candidatures, les dossiers du personnel et la documentation RH pour des évaluations impartiales, des audits de conformité ou des analyses de données, tout en préservant la vie privée.",
        "tone": "warning"
      },
      {
        "title": "Analyse de données",
        "description": "Rendez possible l’analyse de documents contenant des PII en anonymisant au préalable les informations sensibles. Partagez des jeux de données entre équipes sans risque pour la protection des données.",
        "tone": "success"
      },
      {
        "title": "Archivage de documents",
        "description": "Préparez les documents pour l’archivage à long terme en supprimant ou en anonymisant les PII conformément aux politiques de conservation et à la réglementation sur la protection des données.",
        "tone": "secondary"
      },
      {
        "title": "Partage sécurisé",
        "description": "Transmettez des documents à des partenaires externes, à des sous-traitants ou à d’autres services après la suppression automatique des informations personnelles sensibles.",
        "tone": "error"
      }
    ]
  },
  "releases": {
    "title": "Éditions et support",
    "subtitle": "Mises à jour, extensions et support pour une utilisation en production.",
    "items": [
      {
        "title": "Application de bureau, API et AnonChat",
        "description": "Kyveil est disponible sous forme d’application de bureau et d’API REST pour les intégrations. AnonChat est le chat web avec anonymisation préalable des documents.",
        "tone": "info"
      },
      {
        "title": "Support",
        "description": "Pour l’utilisation en entreprise, nous proposons un support technique, une aide à la mise en œuvre et des adaptations.",
        "tone": "success"
      }
    ]
  },
  "cta": {
    "title": "Démarrer avec Kyveil",
    "subtitle": "Protégez vos documents sensibles avant qu’ils ne soient transmis à l’IA, à des tests, à des analyses ou à des tiers.",
    "desktop": "Demander l’application de bureau",
    "desktopSubject": "Kyveil Desktop",
    "apiContact": "Contact pour un déploiement de l’API REST",
    "contact": "Pour une démonstration ou un devis, écrivez-nous.",
    "email": "info@paioneer.eu",
    "apiSubject": "Kyveil - déploiement de l’API REST",
    "contactSubject": "Kyveil - démo ou devis"
  }
};
